/**
 * useDashboardConfig.js - Hook per caricare la configurazione dashboard
 * Legge il profilo utente e carica la config corrispondente da Supabase
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';
import { getDashboardConfig } from '../services/dashboardConfigService';

/**
 * Hook to load dashboard configuration based on user profile
 * @param {string} langOverride - Optional language override (default: 'it')
 * @returns {{ config, loading, error, refetch }}
 */
export const useDashboardConfig = (langOverride = 'it') => {
  const { profile, loading: authLoading } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchConfig = useCallback(async () => {
    if (authLoading) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const role_professional = profile?.role_professional || 'default';
      const user_type = profile?.user_type || null;
      const role_system = profile?.role_system || 'user';
      const lang = langOverride;
      
      console.log('🔄 Loading dashboard config for:', { role_professional, user_type, role_system, lang });
      
      const { data, error: fetchError } = await getDashboardConfig({
        role_professional,
        user_type,
        role_system,
        lang
      });
      
      if (fetchError) {
        console.error('❌ Dashboard config fetch error:', fetchError);
        // Don't set error for 404/empty - use fallback instead
        if (fetchError.code !== 'PGRST116') {
          setError(fetchError);
        }
      }
      
      if (data) {
        console.log('✅ Dashboard config loaded:', data.role_professional);
        setConfig(data);
      } else {
        // No config found - use fallback
        console.log('⚠️ No dashboard config found, using fallback');
        setConfig(null);
      }
    } catch (err) {
      console.error('❌ Dashboard config exception:', err);
      // Use fallback instead of showing error
      setConfig(null);
    } finally {
      setLoading(false);
    }
  }, [profile, authLoading, langOverride]);

  // Fetch config when profile changes
  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // Refetch function
  const refetch = useCallback(() => {
    return fetchConfig();
  }, [fetchConfig]);

  return {
    config,
    loading: loading || authLoading,
    error,
    refetch
  };
};

export default useDashboardConfig;
